import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm, UseFormReturn } from "react-hook-form";
import { usePageLoader } from "../contexts/page_content_context";
import { useDependencyInjection } from "../../config/di/dependency_injection_context";
import { zodResolver } from "@hookform/resolvers/zod";
import { Autocomplete, Box, Divider, Drawer, Paper, TextField } from "@mui/material";
import DynamicIcon from "./dynamic_icon";
import PrimaryButton from "./primary_button";
import DefaultFormInput from "./default_form_input";
import { FormSwitchInput } from "./masked_inputs/react_forms/form_switch_input";

// Depedencias unicas 

import { CargoDropDown } from "../domain/models/cargo_dropdown";
import { CargoForm, CargoSchema, defaultCargoForm } from "../../features/rh/presentation/schemas/Cargos_cadastro/cargo";

type Props<F extends {}> = {
  name: keyof F,
  label?: string,
  newButtonLabel?: string,
  required?: boolean,
  readOnly?: boolean,
  placeholder?: string,
  reactHookForm: UseFormReturn<F>,

};

export const DropDownCargo = <F extends {},>({
  name,
  newButtonLabel,
  required,
  readOnly,
  placeholder,
  reactHookForm,

}: Props<F>) => {


  const [openModal, setOpenModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const { pushErrorNotification, pushInfoNotification } = usePageLoader();


  const [conteudoDropDown, setConteudoDropDown] = useState<CargoDropDown[]>([]);

  const {
    commons: { getCargosDropDownUseCase },
    rh: { createCargoUseCase },
  } = useDependencyInjection();


  const hookForm = useForm<CargoForm>({
    reValidateMode: 'onSubmit',
    mode: 'onSubmit',
    defaultValues: defaultCargoForm,
    resolver: zodResolver(CargoSchema),
  });

  //Create Centro de Custo

  const loadCargoDropDown = useCallback(async () => {
    try {
      const centroCusto = await getCargosDropDownUseCase.getCargosDropDown();
      setConteudoDropDown(centroCusto);
    } catch (e: unknown) {
      console.error("Erro ao carregar cargos:", e);
    }
  }, [getCargosDropDownUseCase]);

  const hanldleSalvarCentroCusto = useCallback(async (form: CargoForm) => {
    try {
      setSaving(true);

      const idCargo = await createCargoUseCase.createCargo(
        form.ativo,
        form.descricao,
        form.atribuicoes,
        form.escolaridade,
        form.experiencia,
        form.qualificacoes,
        form.treinamentos?.map(x => x.idTreinamento) ?? [],
        form.habilidades?.map(x => x.idHabilidade) ?? [],
      );

      await loadCargoDropDown();

      reactHookForm.setValue(name as any, idCargo as any);
      pushInfoNotification("SUCESSO", "Centro custo criado com sucesso!!", { isAutoFinish: true })

    } catch (e: unknown) {
      pushErrorNotification("Erro", ["Falha ao salvar centroCusto"], { isAutoFinish: true })
    } finally {
      setSaving(false);
      setOpenModal(false);
    }
  }, [
    name,
    reactHookForm,
    createCargoUseCase,
    loadCargoDropDown,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  //Load Centro de Custo DropDown

  useEffect(() => {
    loadCargoDropDown();
  }, [loadCargoDropDown]);


  // Add Centro Custo Button

  const CustomPaper = useMemo(
    () =>
      ({ children, ...props }: any) => (
        <Paper {...props}>
          <div>
            {children}
            <Divider />
            <div>
              <div
                className="py-2 px-4 cursor-pointer rounded-[4px] font-semibold flex items-center gap-2 text-sm text-primary-green hover:bg-hover-light-gray"
                onMouseDown={() => setOpenModal(true)}
                role="button"
                tabIndex={0}>
                <DynamicIcon
                  nameIcon="Add"
                  size={20}
                  className="text-primary-green"
                />
                <span>{newButtonLabel ?? "Novo"}</span>
              </div>
            </div>
          </div>
        </Paper>
      ),
    [newButtonLabel]
  );



  return (
    <>

      {/*Centro Custo DropDown */}

      <Controller
        name={name as any}
        control={reactHookForm.control}
        render={({ field, formState }) => {
          let helperText: string | null = null;

          if (formState.errors[name as keyof typeof formState.errors]) {
            const fieldError = formState.errors[name] as any;
            if (fieldError?.message && typeof fieldError.message === 'string') {
              helperText = fieldError.message;
            }
          }


          return (
            <Autocomplete
              fullWidth={true}
              size="small"
              disablePortal
              options={conteudoDropDown}
              getOptionKey={opt => opt.idCargo}
              getOptionLabel={(opt) => opt.descricao ?? ""}
              isOptionEqualToValue={(opt, val) => opt.idCargo === val.idCargo}
              slots={{
                paper: CustomPaper,
              }}
              sx={{ minHeight: 45 }}
              readOnly={readOnly}
              disabled={formState.disabled}
              value={conteudoDropDown.find(x => x.idCargo === field.value) ?? null}
              onChange={(_, newValue) => field.onChange(newValue ? newValue.idCargo : undefined)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Cargo"
                  error={helperText !== null}
                  helperText={helperText}
                  placeholder={placeholder}
                  required={required}
                />
              )}
            />
          );
        }}
      />

      {/* Add Centro Custo Modal */}

      <Drawer
        anchor="right"
        open={openModal}
        onClose={() => setOpenModal(false)}
        style={{ zIndex: 1301 }}
        PaperProps={{ style: { width: 500, padding: 20, display: "flex", flexDirection: "column", height: "100%" } }}
      >
        <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
          <h2 className="text-lg font-semibold mb-4">Adicionar Cargo</h2>


          <div className="flex flex-col items-start w-[25rem] gap-4">
            <FormSwitchInput
              name='ativo'
              readOnly={saving}
              reactHookForm={hookForm} />

            <DefaultFormInput
              name='descricao'
              label='Nome'
              readOnly={saving}
              required={true}
              reactHookForm={hookForm} />

            <DefaultFormInput
              name='atribuicoes'
              label='Atribuições, responsabilidades e autoridades'
              readOnly={saving}
              required={true}
              multiline={true}
              rows={4}
              reactHookForm={hookForm} />

            <DefaultFormInput
              name='escolaridade'
              label='Escolaridade'
              readOnly={saving}
              multiline={true}
              rows={4}
              reactHookForm={hookForm} />

            <DefaultFormInput
              name='experiencia'
              label='Experiência'
              readOnly={saving}
              multiline={true}
              rows={4}
              reactHookForm={hookForm} />

            <DefaultFormInput
              name='qualificacoes'
              label='Qualificações, conhecimentos e habilitações'
              readOnly={saving}
              multiline={true}
              rows={4}
              reactHookForm={hookForm} />
          </div>


        </Box>

        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            width: "100%",
            background: "white",
            padding: 2,
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >

          <Box
            className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold"
            onClick={() => setOpenModal(false)}
          >
            <h1 className="text-sm text-black dark:text-white">Cancelar</h1>
          </Box>


          <Box width="120px">
            {saving ? (
              <PrimaryButton buttonColor="primary-green">
                Salvando...
              </PrimaryButton>
            ) : (
              <PrimaryButton
                buttonColor="primary-green"
                onClick={hookForm.handleSubmit(hanldleSalvarCentroCusto)}
              >
                Salvar
              </PrimaryButton>
            )}
          </Box>
        </Box>
      </Drawer>

    </>
  );
};
